import { Card, Carousel, Col, Row, ConfigProvider, Skeleton } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import InformacionParaEgresados from "./../../../../hooks/hookAvisos";
import { colorCONALEP } from "./../../../../../../config/AppConfig";

const { Meta } = Card;

// Estilos para mantener altura consistente y diseño uniforme
const cardContainerStyle = {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
};


// Área de portada (imagen o fondo)
const coverStyle = {
    width: "100%",
    height: "200px", // Altura fija para unificar todas las tarjetas
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#cececeff",
    backgroundImage: "linear-gradient(45deg, #cececeff 25%, transparent 25%)",
    backgroundSize: "20px 20px",
    backgroundRepeat: "repeat",
    overflow: "hidden", 
    position: "relative",
};

// Estilos de la imagen dentro del Card
const imgStyle = {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "cover",
    objectPosition: "center",
};

// 🔗 Enlace externo con ícono
const ExternalLink = ({ href, children }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            color: "#1677ff",
            marginTop: "10px",
        }}
    >
        <LinkOutlined />
        {children}
    </a>
);

// 🧱 Componente individual de tarjeta
const AppCard = ({ title, description, src, link, linkText }) => (
    <Card
        style={cardContainerStyle}
        cover={
            src ? (
                <div style={coverStyle}>
                    <img alt="cover" src={src} style={imgStyle} />
                </div>
            ) : (
                <div style={{ height: "100%", backgroundImage: "none" }}>
                    <span style={{ color: "#fff" }}>Sin imagen</span>
                </div>
            )
        }
    >
        <Meta
            title={
                <span
                    style={{
                        color: "black",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                    }}
                >
                    {title}
                </span>
            }
            description={description}
        />
        {link && <ExternalLink href={link}>{linkText}</ExternalLink>}
    </Card>
);

// 🎠 Carrusel principal con manejo de carga y agrupación
const Avisos = ({ cardsData = InformacionParaEgresados }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);

    // Simula carga o usa hook real
    useEffect(() => {
        const timeout = setTimeout(() => {
            setData(cardsData);
            setLoading(false);
        }, 1500);

        return () => clearTimeout(timeout);
    }, [cardsData]);

    // Agrupar en bloques de 3 tarjetas por slide
    const groupCardsInChunks = (cards, chunkSize = 3) => {
        const chunks = [];
        for (let i = 0; i < cards.length; i += chunkSize) {
            chunks.push(cards.slice(i, i + chunkSize));
        }
        return chunks;
    };

    const cardChunks = groupCardsInChunks(data, 3);

    const defaultConfig = {
        dots: true,
        autoplay: true, 
        effect: "slide",
    };

    return (
        <Row style={{ marginTop: "50px" }}>
            <Col xs={24}>
                <ConfigProvider
                    theme={{
                        components: {
                            Carousel: {
                                dotGap: 12,
                                dotOffset: 16,
                                dotHeight: 5,
                                colorBgContainer: colorCONALEP,
                                dotActiveWidth: 28,
                            },
                        },
                    }}
                >
                    <Carousel
                        id="carrucel_egresados"
                        dots={{ className: "custom-carousel-dots" }}
                        {...defaultConfig}
                        style={{
                            width: "100%",
                            maxWidth: "1020px",
                            margin: "0 auto",
                        }}
                    >
                        {loading
                            ? [0, 1].map((skeletonChunkIndex) => (
                                <div key={`skeleton-chunk-${skeletonChunkIndex}`}>
                                    <Row
                                        gutter={[8, 8]}
                                        align="center"
                                        justify="center"
                                        style={{
                                            paddingBottom: "30px",
                                            margin: "0 30px",
                                        }}
                                    >
                                        {[0, 1, 2].map((skeletonCardIndex) => (
                                            <Col
                                                key={`skeleton-${skeletonChunkIndex}-${skeletonCardIndex}`}
                                                xs={24}
                                                sm={12}
                                                md={8}
                                                lg={8}
                                                style={{ display: "flex" }}
                                            >
                                                <Card style={{ width: "100%" }}> 
                                                    <Skeleton active paragraph={{ rows: 7 }} />
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            ))
                            : cardChunks.map((chunk, chunkIndex) => (
                                <div key={`chunk-${chunkIndex}`}>
                                    <Row
                                        gutter={[8, 8]}
                                        align="center"
                                        justify="center"
                                        style={{
                                            paddingBottom: "30px",
                                            margin: "0 30px",
                                        }}
                                    >
                                        {chunk.map((card, cardIndex) => (
                                            <Col
                                                key={card.id || card.title || `${chunkIndex}-${cardIndex}`}
                                                xs={24}
                                                sm={12}
                                                md={8}
                                                lg={8}
                                                style={{ display: "flex" }}
                                            >
                                                <AppCard
                                                    title={card.title}
                                                    description={card.description}
                                                    src={card.src}
                                                    link={card.link}
                                                    linkText={card.linkText || "Ir al sitio"}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            ))}
                    </Carousel>
                </ConfigProvider>
            </Col>
        </Row>
    );
};

export default Avisos;
